import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_MONEY_ADJUSTMENT_DETAILS, ROUTE_MONEY_ADJUSTMENT_LIST } from '../../constants/routeConstants';
import * as Yup from 'yup';
import { Button, Card } from 'react-bootstrap';
import { customStorage, getCurrentJSTDate, getPayloadData, isNullOrEmptyString, isNullOrUndefinedObj, scrollToFormError } from '../../services/UtilityService';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import AlertDialog from '../../share-components/AlertDialog';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import { getAllActiveUsers, getLastLockedYearMonth } from '../../services/CommonService';
import FormTitle from '../../share-components/FormTitle';

// ? NEW IMPORTS
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import CalendarInput from '../../share-components/CalendarInput';
import { editMoneyAdjustment, getMoneyAdjustmentDetails } from '../../services/MoneyAdjustmentService';

const getLastMonthDate = () => {
    const jst = getCurrentJSTDate();
    jst.setMonth(jst.getMonth() - 1); // subtract 1 month
    return jst;
};

const EditMoneyAdjustment = () => {
    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const { moneyAdjustmentId } = useParams();
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [formValue, setFormValue] = useState(null);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [editedMoneyAdjustmentId, setEditedMoneyAdjustmentId] = useState(null);
    const [lastLockedYearMonth, setLastLockedYearMonth] = useState(null);
    const [minDate, setMinDate] = useState(getLastMonthDate);
    const [moneyAdjustmentData, setMoneyAdjustmentData] = useState(null);
    const [activeUsers, setActiveUsers] = useState([]);
    const navigate = useNavigate();

    const now = getCurrentJSTDate();
    const maxDate = now;
    const today = maxDate;

    const validateEditProcess = () => {
        const dateStr = moneyAdjustmentData?.paymentDate || '';
        const paymentYear = dateStr.length >= 4 ? parseInt(dateStr.slice(0, 4), 10) : 0;
        const paymentMonth = dateStr.length >= 6 ? parseInt(dateStr.slice(4, 6), 10) : 0;
        const isValidUser = isNullOrEmptyString(moneyAdjustmentData?.paidById) || moneyAdjustmentData?.paidById === currentUser?.userId;
        const isValidLockDate = isNullOrUndefinedObj(lastLockedYearMonth) || lastLockedYearMonth.toYear < paymentYear ||
            (lastLockedYearMonth.toYear === paymentYear && lastLockedYearMonth.toMonth < paymentMonth);
        const isValid = isValidUser && isValidLockDate;
        if (!isValid) {
            navigate(`${ROUTE_MONEY_ADJUSTMENT_DETAILS}/${moneyAdjustmentId}`);
        }
    }

    const initialLoad = async () => {
        setLoading(true);
        try {
            const users = await getAllActiveUsers();
            const sortedUsers = (users || [])?.sort((a, b) => (a.username || "")?.localeCompare((b?.username || "")));
            const filteredUser = sortedUsers.filter(user => user.userId !== currentUser?.userId);
            setActiveUsers(filteredUser);

            const response = await getMoneyAdjustmentDetails((parseInt(moneyAdjustmentId) || 0));
            const moneyAdjustmentDetails = response.moneyAdjustmentDetailsInfo;
            if (moneyAdjustmentDetails?.paidById !== currentUser?.userId) {
                navigate(`${ROUTE_MONEY_ADJUSTMENT_DETAILS}/${moneyAdjustmentId}`);
            }
            const paymentYear = parseInt(moneyAdjustmentDetails?.paymentDate?.slice(0, 4), 10);
            const paymentMonth = parseInt(moneyAdjustmentDetails?.paymentDate?.slice(4, 6), 10);

            const lastLockInfo = await getLastLockedYearMonth();
            setLastLockedYearMonth(lastLockInfo || null);
            if (lastLockInfo) {
                const minDate = new Date(lastLockInfo?.toYear, (parseInt(lastLockInfo?.toMonth) || 0), 1);
                setMinDate(minDate);
            } else {
                if (!!moneyAdjustmentDetails) {
                    const minDate = new Date(paymentYear, paymentMonth - 2, 1);
                    setMinDate(minDate);
                }
            }
            setMoneyAdjustmentData(moneyAdjustmentDetails);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRouteNaviData("MONEY ADJUSTMENT >> EDIT ADJUSTMENT");
        initialLoad();
    }, []);

    useEffect(() => {
        validateEditProcess();
    }, [moneyAdjustmentData]);

    const getOldDate = () => {
        const dateStr = moneyAdjustmentData?.paymentDate;
        if (!dateStr) {
            return '';
        }

        const year = parseInt(dateStr.substring(0, 4), 10);
        const month = parseInt(dateStr.substring(4, 6), 10) - 1; // month is 0-based in JS Date
        const day = parseInt(dateStr.substring(6, 8), 10);

        const dateObj = new Date(year, month, day);
        return dateObj;
    }

    const initialValues = {
        paymentAmount: moneyAdjustmentData?.paymentAmount || '',
        paymentDate: getOldDate(),
        acceptedById: moneyAdjustmentData?.acceptedById || '',
        comment: moneyAdjustmentData?.comment || ''
    };

    const validationSchema = Yup.object({
        paymentAmount: Yup.number().nullable()
            .required('Payment Amount is required.')
            .transform((value) => (Number.isNaN(value) ? null : value))
            .min(1, 'Payment Amount must exceed 0.')
            .max(1000000, 'Payment Amount must not exceed 1000000.'),
        paymentDate: Yup.date()
            .required('Payment Date is required.')
            .max(maxDate, 'Payment Date cannot be in the future.')
            .min(minDate, `Payment Date cannot be before ${format(minDate, 'yyyyMMdd')}`),
        acceptedById: Yup.string().nullable()
            .required('Accepted By is required.'),
        comment: Yup.string().nullable()
            .max(300, 'Comment must not exceed 300 characters.')
    });

    const onSubmit = async (form) => {
        const payload = {
            moneyAdjustmentId,
            ...form,
            paymentDate: format(form.paymentDate, 'yyyyMMdd') // ? send formatted string
        };
        setFormValue(payload);
        setShowConfirmDialog(true);
    };

    const handleEditMoneyAdjustment = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            const editedId = await editMoneyAdjustment(formValue);
            setEditedMoneyAdjustmentId(editedId);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        if ((parseInt(editedMoneyAdjustmentId) || 0) > 0) {
            navigate(`${ROUTE_MONEY_ADJUSTMENT_DETAILS}/${editedMoneyAdjustmentId}`);
        } else {
            navigate(ROUTE_MONEY_ADJUSTMENT_LIST);
        }
    };

    return (
        <div id='editMoneyAdjustmentPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"Edit Adjustment"} isCenter={true} />

            {isFirstLoaded && <>
                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                    <Card.Body className='pt-2'>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div style={{ maxWidth: "550px" }}>
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={onSubmit}>
                                        {({ setFieldValue, values }) => (
                                            <Form>
                                                <div className="mb-1">
                                                    <label className='custom-label'>Payment Amount*</label>
                                                    <Field
                                                        name="paymentAmount"
                                                        type="number"
                                                        inputMode="numeric"  // forces numeric keypad on mobile
                                                        pattern="[0-9]*"      // enforces only digits
                                                        className="w-100 customInputControl clearNumberSpin"
                                                        onKeyDown={(e) => {
                                                            if (["e", "-", ".", "+"].includes(e.key)) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                    <ErrorMessage name="paymentAmount" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className="mb-1">
                                                    <label className='custom-label'>Payment Date(yyyyMMdd)*</label><br />
                                                    <DatePicker
                                                        selected={values.paymentDate}
                                                        onChange={(date) => setFieldValue("paymentDate", date)}
                                                        dateFormat="yyyy/MM/dd"
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        placeholderText="Select date"
                                                        customInput={<CalendarInput isReadOnly={true} />}  // 👈 here
                                                        dayClassName={(date) =>
                                                            date.getDate() === today.getDate() &&
                                                                date.getMonth() === today.getMonth() &&
                                                                date.getFullYear() === today.getFullYear()
                                                                ? "current-day"
                                                                : undefined
                                                        }
                                                    />
                                                    <ErrorMessage name="paymentDate" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className="mb-1">
                                                    <label className="custom-label">Accepted By*</label>
                                                    <Field as="select" name="acceptedById" className="w-100 customInputControl">
                                                        <option value="" disabled>--Select--</option>
                                                        {activeUsers.map(user => (
                                                            <option key={user.userId} value={user.userId}>{user.username}</option>
                                                        ))}
                                                    </Field>
                                                    <ErrorMessage name="acceptedById" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className="mb-1">
                                                    <label className='custom-label'>Comment</label>
                                                    <Field name="comment" component="textarea" rows="4" className="w-100 customInputControl" autoComplete="off" />
                                                    <ErrorMessage name="comment" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className='floating-bottom-btn-group mt-2'>
                                                    <div className='floating-bottom-btn-grid'>
                                                        <Button
                                                            className="w-100"
                                                            type="submit"
                                                            variant="primary"
                                                            onClick={scrollToFormError}
                                                        >
                                                            Save
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <ConfirmDialog
                    dialogId='editMoneyAdjustmentConfirmDialog'
                    showDialog={showConfirmDialog}
                    body='Are you sure to edit this money adjustment?'
                    onConfirm={handleEditMoneyAdjustment}
                    onCancel={() => { setFormValue(null); setShowConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={'Your money adjustment has been successfully edited.'}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
};

export default EditMoneyAdjustment;
